import React, { useEffect, useRef, lazy, memo, createElement } from 'react';
import { LazyMotion, domAnimation, m } from 'framer-motion';
import { Shield, Lock, CreditCard, ArrowDown, Check, HelpCircle } from 'lucide-react';
import { UrgencyBar } from '../components/UrgencyBar';
import { PurchasePopup } from '../components/PurchasePopup';
import { ComboSelector, ComboId } from '../components/ComboSelector';
import { TestimonialCard } from '../components/TestimonialCard';
// Meta Pixel ID
const META_PIXEL_ID = '1927478928649366';
// Declare fbq for TypeScript
declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}
// Image URLs
const MAIN_PRODUCT_IMG = 'https://cdn.magicpatterns.com/uploads/7B6zajucgKVzUvErVHwcwh/ChatGPT_Image_27_ene_2026,_02_13_39_(1)_(2).png';
const MERCADO_LIBRE_IMG = 'https://cdn.magicpatterns.com/uploads/aSqE9gyQNM7hvR9VAB7BpK/ML.jpg';
export function SalesPage() {
  const checkoutRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    document.title = 'LENGUAJE CORPORAL';
    // Defer Meta Pixel loading until after initial render
    const deferPixel = () => {
      const initMetaPixel = () => {
        const f = window as any;
        if (f.fbq) return;
        const n: any = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        const t = document.createElement('script');
        t.async = true;
        t.src = 'https://connect.facebook.net/en_US/fbevents.js';
        const s = document.getElementsByTagName('script')[0];
        if (s && s.parentNode) {
          s.parentNode.insertBefore(t, s);
        } else {
          document.head.appendChild(t);
        }
        f.fbq('init', META_PIXEL_ID);
        f.fbq('track', 'PageView');
      };
      initMetaPixel();
      const existingNoscript = document.querySelector(`img[src*="facebook.com/tr?id=${META_PIXEL_ID}"]`);
      if (!existingNoscript) {
        const img = document.createElement('img');
        img.height = 1;
        img.width = 1;
        img.style.display = 'none';
        img.src = `https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`;
        document.body.appendChild(img);
      }
    };
    // Use requestIdleCallback to load pixel when browser is idle, fallback to setTimeout
    if ('requestIdleCallback' in window) {
      ;
      (window as any).requestIdleCallback(deferPixel);
    } else {
      setTimeout(deferPixel, 2500);
    }
    const updateMetaTag = (property: string, content: string) => {
      let element = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', property);
        document.head.appendChild(element);
      }
      element.content = content;
    };
    updateMetaTag('og:title', 'LENGUAJE CORPORAL');
    updateMetaTag('og:description', 'Descubre el sistema de 3 capas para leer personas, proyectar autoridad y defenderte de la manipulación.');
    updateMetaTag('twitter:title', 'LENGUAJE CORPORAL');
    updateMetaTag('twitter:description', 'Descubre el sistema de 3 capas para leer personas, proyectar autoridad y defenderte de la manipulación.');
  }, []);
  const scrollToCheckout = () => {
    checkoutRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handleBuy = (comboNumber: ComboId) => {
    const checkoutLinks = {
      1: 'https://checkout3.rosetadigital.com/cart/45956744675513:1',
      2: 'https://checkout3.rosetadigital.com/cart/45959149551801:1',
      3: 'https://checkout3.rosetadigital.com/cart/45959188447417:1'
    };
    const prices = {
      1: 19990,
      2: 29990,
      3: 39990
    };
    const comboNames = {
      1: 'Combo 1 – Lector Humano',
      2: 'Combo 2 – Influencia Total',
      3: 'Combo 3 – Dominio Mental'
    };
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_name: comboNames[comboNumber],
        content_category: 'Ebook',
        content_ids: [`combo_${comboNumber}`],
        content_type: 'product',
        value: prices[comboNumber],
        currency: 'ARS'
      });
    }
    window.location.href = checkoutLinks[comboNumber];
  };
  return <LazyMotion features={domAnimation} strict data-id="element-108">
      <div className="min-h-screen bg-black text-gray-200 font-sans selection:bg-yellow-500 selection:text-black" data-id="element-109">
        <UrgencyBar data-id="element-110" />
        <PurchasePopup data-id="element-111" />

        {/* Hero Section */}
        <header className="relative pt-12 pb-20 px-4 overflow-hidden" data-id="element-112">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-gray-800/20 via-black to-black z-0" data-id="element-113"></div>
          <div className="max-w-4xl mx-auto relative z-10 text-center" data-id="element-114">
            <m.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-115">
              <h2 className="text-yellow-500 font-bold uppercase tracking-widest text-sm mb-4" data-id="element-116">
                Lo que nadie te enseñó sobre las personas
              </h2>
              <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-8 leading-tight" data-id="element-117">
                En el mundo real, no gana el que habla más.
                <br data-id="element-118" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600" data-id="element-119">
                  Gana el que entiende mejor.
                </span>
              </h1>
            </m.div>

            <m.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.3,
            duration: 0.6
          }} className="mb-10 relative inline-block group cursor-pointer" onClick={scrollToCheckout} data-id="element-120">
              <div className="absolute -inset-1 bg-gradient-to-r from-yellow-600 to-yellow-400 rounded-lg blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200" data-id="element-121"></div>
              <img src={MAIN_PRODUCT_IMG} alt="Lenguaje Corporal Libro Principal" width={600} height={900} fetchPriority="high" decoding="async" className="relative rounded-lg shadow-2xl max-w-full h-auto md:max-h-[500px] mx-auto transform group-hover:scale-[1.01] transition-transform duration-300" data-id="element-122" />
            </m.div>

            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-10" data-id="element-123">
              Descubre el sistema de 3 capas para leer personas, proyectar
              autoridad y defenderte de la manipulación.
            </p>

            <m.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={scrollToCheckout} className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-xl py-4 px-8 md:px-12 rounded-full shadow-[0_0_20px_rgba(253,185,19,0.4)] uppercase tracking-wide flex items-center justify-center gap-2 mx-auto w-full md:w-auto" data-id="element-124">
              Descargar el Manual Ahora <ArrowDown className="w-6 h-6" data-id="element-125" />
            </m.button>
          </div>
        </header>

        {/* Sales Letter Content */}
        <section className="py-12 px-4 bg-zinc-900/30" data-id="element-126">
          <div className="max-w-2xl mx-auto prose prose-invert prose-lg" data-id="element-127">
            <p className="text-xl font-medium text-white" data-id="element-128">
              Desde chico te enseñaron a hablar.
            </p>
            <p data-id="element-129">A ser educado. A confiar. A ser correcto.</p>
            <p className="font-bold text-white text-2xl border-l-4 border-yellow-500 pl-4 my-8" data-id="element-130">
              Pero nunca te enseñaron a leer personas.
            </p>
            <p data-id="element-131">Y eso… lo pagaste caro.</p>
            <p data-id="element-132">
              Tal vez confiando en quien no debías. Tal vez callando cuando
              tenías que imponerte. Tal vez eligiendo mal. Tal vez perdiendo
              oportunidades.
            </p>

            <h3 className="text-3xl font-bold text-white mt-12 mb-6" data-id="element-133">
              El Problema No Sos Vos
            </h3>
            <p data-id="element-134">Durante años pensé que era "mala suerte".</p>
            <p data-id="element-135">
              Que siempre me tocaban personas complicadas. Que siempre llegaba
              tarde a darme cuenta. Que siempre reaccionaba después.
            </p>
            <p data-id="element-136">Hasta que entendí algo:</p>
            <p className="text-yellow-400 font-bold text-xl" data-id="element-137">
              No veía lo que estaba frente a mí.
            </p>
            <p data-id="element-138">Señales. Gestos. Cambios. Microexpresiones.</p>
            <p data-id="element-139">Todo estaba ahí. Yo no sabía leerlo.</p>

            <h3 className="text-3xl font-bold text-white mt-12 mb-6" data-id="element-140">
              Probé Todo… Y Nada Funcionó
            </h3>
            <p data-id="element-141">
              Videos en YouTube. Frases motivacionales. Libros densos. Cursos
              carísimos.
            </p>
            <p data-id="element-142">Nada conectaba. Todo era teoría. Nada era sistema.</p>
            <p data-id="element-143">Hasta que empecé a unir piezas.</p>

            <div className="bg-gray-900 p-8 rounded-xl border border-gray-800 my-12" data-id="element-144">
              <h3 className="text-2xl font-bold text-yellow-400 mt-0 mb-4" data-id="element-145">
                El Descubrimiento
              </h3>
              <p className="mb-4" data-id="element-146">
                No necesitás más información. Necesitás estructura.
              </p>
              <p className="font-bold text-white mb-2" data-id="element-147">Tres capas:</p>
              <div className="flex gap-4 mb-6 text-white font-bold" data-id="element-148">
                <span className="bg-gray-800 px-3 py-1 rounded" data-id="element-149">1️⃣ Leer</span>
                <span className="bg-gray-800 px-3 py-1 rounded" data-id="element-150">
                  2️⃣ Proyectar
                </span>
                <span className="bg-gray-800 px-3 py-1 rounded" data-id="element-151">
                  3️⃣ Defenderte
                </span>
              </div>
              <p className="m-0" data-id="element-152">Si falta una, perdés.</p>
            </div>

            <h3 className="text-3xl font-bold text-white mt-12 mb-6" data-id="element-153">
              Por eso nació este manual completo de lenguaje corporal
            </h3>
            <p data-id="element-154">Un sistema completo para:</p>
            <ul className="list-none pl-0 space-y-2" data-id="element-155">
              <li className="flex items-center gap-2" data-id="element-156">
                <Check className="w-5 h-5 text-yellow-500 flex-shrink-0" data-id="element-157" />{' '}
                Entender personas
              </li>
              <li className="flex items-center gap-2" data-id="element-158">
                <Check className="w-5 h-5 text-yellow-500 flex-shrink-0" data-id="element-159" />{' '}
                Impactar sin forzar
              </li>
              <li className="flex items-center gap-2" data-id="element-160">
                <Check className="w-5 h-5 text-yellow-500 flex-shrink-0" data-id="element-161" />{' '}
                Evitar manipulaciones
              </li>
            </ul>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-8 px-4 bg-black" data-id="element-162">
          <div className="max-w-3xl mx-auto text-center" data-id="element-163">
            <h2 className="text-2xl font-bold text-white mb-5" data-id="element-164">
              Qué Va a Cambiar en Vos
            </h2>

            <div className="flex flex-wrap justify-center gap-2 mb-5" data-id="element-165">
              {[{
              title: 'Notar cosas antes',
              icon: '👁️'
            }, {
              title: 'Dudar menos',
              icon: '⚡'
            }, {
              title: 'Elegir mejor',
              icon: '🎯'
            }, {
              title: 'Decidir con claridad',
              icon: '🧠'
            }, {
              title: 'Ser respetado',
              icon: '👑'
            }, {
              title: 'Sin fingir',
              icon: '🎭'
            }].map((item, idx) => <span key={idx} className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 text-sm font-medium text-white inline-flex items-center gap-1.5" data-id="element-166">
                  <span className="text-lg" data-id="element-167">{item.icon}</span> {item.title}
                </span>)}
            </div>

            <p className="text-gray-400 text-sm" data-id="element-168">
              Esto no es motivación. Es{' '}
              <span className="text-white font-bold" data-id="element-169">entrenamiento mental</span>
              . Como ir al gimnasio, pero para tu vida social.
            </p>
          </div>
        </section>

        {/* Bonuses Section (Compact) */}
        <section className="py-12 px-4 bg-zinc-900" data-id="element-170">
          <div className="max-w-3xl mx-auto" data-id="element-171">
            <div className="text-center mb-8" data-id="element-172">
              <span className="bg-red-600 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider" data-id="element-173">
                Gratis por hoy
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-white mt-3 mb-2" data-id="element-174">
                Tus 3 Bonus Exclusivos
              </h2>
              <p className="text-gray-400 text-sm" data-id="element-175">
                Te los llevás gratis con cualquier combo.
              </p>
            </div>

            <div className="bg-gray-900/80 border border-gray-800 rounded-xl overflow-hidden divide-y divide-gray-800" data-id="element-176">
              {[{
              title: 'Lectura Rápida en la Vida Real',
              desc: 'Escaneá cualquier situación social en segundos.',
              value: '9.990'
            }, {
              title: 'Influencia y Persuasión Invisible',
              desc: 'Técnicas sutiles para que tus ideas sean aceptadas.',
              value: '12.990'
            }, {
              title: 'Detector de Mentiras y Manipulación',
              desc: 'Microexpresiones que delatan el engaño al instante.',
              value: '14.990'
            }].map((bonus, idx) => <div key={idx} className="flex items-center gap-4 px-5 py-4" data-id="element-177">
                  <div className="shrink-0 w-9 h-9 rounded-lg bg-green-500/15 flex items-center justify-center" data-id="element-178">
                    <span className="text-lg" data-id="element-179">🎁</span>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-180">
                    <h3 className="text-white font-bold text-sm leading-tight" data-id="element-181">
                      BONUS {idx + 1}: {bonus.title}
                    </h3>
                    <p className="text-gray-500 text-xs mt-0.5" data-id="element-182">{bonus.desc}</p>
                  </div>
                  <div className="shrink-0 text-right" data-id="element-183">
                    <p className="text-gray-600 text-xs line-through" data-id="element-184">
                      ${bonus.value}
                    </p>
                    <p className="text-green-400 font-bold text-xs" data-id="element-185">GRATIS</p>
                  </div>
                </div>)}
            </div>
          </div>
        </section>

        {/* Pricing / Checkout Section with ComboSelector */}
        <section ref={checkoutRef} className="py-10 px-4 bg-gradient-to-b from-black to-gray-900 relative" data-id="element-186">
          <div className="max-w-6xl mx-auto" data-id="element-187">
            <div className="text-center mb-6" data-id="element-188">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-2" data-id="element-189">
                Elige Tu Nivel de Dominio
              </h2>
              <p className="text-base text-gray-400" data-id="element-190">
                Descarga inmediata. Acceso de por vida.
              </p>

              <div className="flex items-center justify-center gap-2 mt-3 text-red-500 font-bold bg-red-500/10 inline-block px-3 py-1.5 rounded-lg mx-auto text-sm" data-id="element-191">
                <span className="animate-pulse" data-id="element-192">●</span> Solo quedan 7 copias
                con estos bonus
              </div>
            </div>

            <ComboSelector onBuy={handleBuy} data-id="element-193" />
          </div>
        </section>

        {/* Guarantee Section — After Checkout (Access Issues) */}
        <section className="py-16 px-4 bg-black" data-id="element-194">
          <div className="max-w-3xl mx-auto text-center bg-gradient-to-b from-gray-900 to-black border border-gray-700 rounded-2xl p-8 md:p-12 shadow-2xl" data-id="element-195">
            <Shield className="w-16 h-16 text-yellow-500 mx-auto mb-6" data-id="element-196" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-197">
              GARANTÍA DE 7 DÍAS POR INCONVENIENTES DE ACCESO
            </h2>
            <p className="text-lg text-gray-300 mb-6" data-id="element-198">
              Si después de tu compra tenés algún problema para acceder o
              descargar el material, escribinos y lo resolvemos al instante. Si
              no logramos solucionarlo en 7 días, te devolvemos el 100% de tu
              dinero.
            </p>
            <div className="w-16 h-1 bg-yellow-500 mx-auto mb-6 rounded-full" data-id="element-199"></div>
            <p className="text-gray-400 italic" data-id="element-200">
              Tu compra está protegida. Si no podés acceder al contenido, no
              pagás.
            </p>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20 px-4 bg-black" data-id="element-201">
          <div className="max-w-6xl mx-auto" data-id="element-202">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-12" data-id="element-203">
              Lo Que Dicen Quienes Ya Lo Leyeron
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-204">
              <TestimonialCard name="Martín González" role="Vendedor, 34 años" content="Siempre sentía que perdía en las negociaciones. Con el Combo 2 entendí que mi cuerpo decía 'soy débil' antes de abrir la boca. Cambió mi carrera." data-id="element-205" />
              <TestimonialCard name="Laura Fernández" role="Gerente de RRHH, 41 años" content="El módulo de detección de mentiras es escalofriante. Ahora veo cosas en las entrevistas que antes pasaba por alto. Imprescindible." data-id="element-206" />
              <TestimonialCard name="Javier Ruiz" role="Emprendedor, 29 años" content="No es teoría aburrida. Lo leí a la tarde y a la noche ya estaba probando cosas en una cena. Funciona. Punto." data-id="element-207" />
              <TestimonialCard name="Sofía Méndez" role="Abogada, 38 años" content="La psicología oscura me abrió los ojos. Me di cuenta de cómo un colega me manipulaba sutilmente. Nunca más." data-id="element-208" />
              <TestimonialCard name="Diego Torres" role="Estudiante, 24 años" content="Era muy tímido y no sabía dónde poner las manos. El libro de carisma es oro puro. Me siento otra persona." data-id="element-209" />
            </div>
          </div>
        </section>

        {/* Resolved Objections Section — After Checkout */}
        <section className="py-20 px-4 bg-zinc-900" data-id="element-210">
          <div className="max-w-3xl mx-auto" data-id="element-211">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12" data-id="element-212">
              Respuestas a tus dudas
            </h2>
            <div className="space-y-4" data-id="element-213">
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-6" data-id="element-214">
                <h4 className="text-xl font-bold text-white mb-3 flex items-center gap-3" data-id="element-215">
                  <HelpCircle className="text-yellow-500 w-6 h-6 shrink-0" data-id="element-216" />
                  ¿Y si es muy básico?
                </h4>
                <p className="text-gray-400 pl-9" data-id="element-217">
                  No es un libro de tips genéricos. Es un sistema de 3 capas con
                  ejercicios progresivos. Sirve si nunca leíste nada o si ya
                  tenés conocimientos previos y querés estructurarlos.
                </p>
              </div>

              <div className="bg-gray-900 border border-gray-800 rounded-xl p-6" data-id="element-218">
                <h4 className="text-xl font-bold text-white mb-3 flex items-center gap-3" data-id="element-219">
                  <HelpCircle className="text-yellow-500 w-6 h-6 shrink-0" data-id="element-220" />
                  ¿Y si no puedo aplicarlo?
                </h4>
                <p className="text-gray-400 pl-9" data-id="element-221">
                  Cada capítulo tiene ejercicios para practicar el mismo día en
                  situaciones reales (trabajo, citas, familia). No necesitás
                  memorizar teoría compleja.
                </p>
              </div>

              <div className="bg-gray-900 border border-gray-800 rounded-xl p-6" data-id="element-222">
                <h4 className="text-xl font-bold text-white mb-3 flex items-center gap-3" data-id="element-223">
                  <HelpCircle className="text-yellow-500 w-6 h-6 shrink-0" data-id="element-224" />
                  ¿Es humo motivacional?
                </h4>
                <p className="text-gray-400 pl-9" data-id="element-225">
                  Cero frases de autoayuda. Todo es técnica aplicable:
                  microexpresiones, posturas, patrones de engaño y tácticas de
                  proyección de autoridad.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </LazyMotion>;
}